import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { updateUser, getImageUrl, API_BASE_URL } from '../../model/api.js';
import ImageCropper from './common/ImageCropper.jsx';

const ProfileTab = ({ user, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        nominee_name: '',
        nominee_phone: '',
        nominee_dob: '',
        nominee_relationship: '',
        nominee_relationship_custom: ''
    });

    // Photo upload state
    const [showCropper, setShowCropper] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [photoTimestamp, setPhotoTimestamp] = useState(Date.now()); // Cache-buster

    useEffect(() => {
        if (user) {
            // Check if the relationship is a custom one (not in predefined list)
            const predefinedRelationships = ['Spouse', 'Parent', 'Child', 'Sibling', ''];
            const isCustom = user.nominee_relationship && !predefinedRelationships.includes(user.nominee_relationship);

            setFormData({
                email: user.email || '',
                phone: user.phone || '',
                nominee_name: user.nominee_name || '',
                nominee_phone: user.nominee_phone || '',
                nominee_dob: user.nominee_dob ? new Date(user.nominee_dob).toISOString().split('T')[0] : '',
                nominee_relationship: isCustom ? 'Other' : (user.nominee_relationship || ''),
                nominee_relationship_custom: isCustom ? user.nominee_relationship : ''
            });
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Only send fields that are editable
            const updateData = {
                email: formData.email,
                phone: formData.phone,
                nominee_name: formData.nominee_name,
                nominee_phone: formData.nominee_phone,
                nominee_dob: formData.nominee_dob,
                nominee_relationship: formData.nominee_relationship === 'Other' ? formData.nominee_relationship_custom : formData.nominee_relationship
            };

            const result = await updateUser(user._id || user.id, updateData);

            if (result && (result.ok || result._id || result.data?._id)) {
                toast.success('Profile updated successfully!');
                setIsEditing(false);
                if (onUpdate) onUpdate(); // Refresh parent data (in App.jsx)
            } else {
                toast.error(result?.error || result?.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Profile update error:', error);

            // Extract error message from backend response
            const errorMessage = error.response?.data?.message
                || error.response?.data?.error
                || error.message
                || 'Failed to update profile';

            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setSelectedImage(reader.result);
                setShowCropper(true);
            });
            reader.readAsDataURL(file);
            // Reset input so same file can be selected again
            e.target.value = '';
        }
    };

    const handleCropComplete = async (croppedBlob) => {
        setShowCropper(false);
        setPhotoUploading(true);
        try {
            // Create a File object from the blob
            const file = new File([croppedBlob], "profile_photo.jpg", { type: "image/jpeg" });
            const formData = new FormData();
            formData.append('profile_photo', file);
            const result = await updateUser(user._id || user.id, formData);


            // Check if upload was successful
            // Backend returns { ok: true, data: userObject, message: "..." } on success
            // Handle multiple possible response structures:
            // 1. { ok: true, data: {...}, message: "..." }
            // 2. { data: { ok: true, data: {...} } } (double-wrapped)
            // 3. Direct user object with _id
            const isSuccess =
                result?.ok === true ||
                result?.data?.ok === true ||
                result?.data?._id ||
                result?._id;



            if (isSuccess) {
                toast.success('Profile photo updated successfully!');

                // Update the cache-buster to force image reload
                setPhotoTimestamp(Date.now());

                // Also call parent's onUpdate to refresh from server
                if (onUpdate) onUpdate();
            } else {
                // Only show error if we're certain it failed
                const errorMessage = result?.error || result?.data?.error || result?.message || result?.data?.message || 'Failed to update profile photo';
                console.error('Upload failed:', errorMessage);
                console.error('Full result object:', JSON.stringify(result, null, 2));
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Photo upload error:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to upload profile photo');
        } finally {
            setPhotoUploading(false);
            setSelectedImage(null);
        }
    };

    const handleCancelCrop = () => {
        setShowCropper(false);
        setSelectedImage(null);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">My Profile</h2>

            {/* Profile Header Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    {/* Photo Section */}
                    <div className="relative group">
                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100 flex items-center justify-center">
                            {user.profile_photo_url ? (
                                <>
                                    {/* Profile photo loaded with cache-buster */}
                                    <img
                                        src={`${API_BASE_URL}/users/${user._id || user.id}/photo?t=${photoTimestamp}`}
                                        alt={user.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            console.error('❌ Failed to load profile photo via proxy');
                                            e.target.style.display = 'none';
                                        }}
                                    />
                                </>
                            ) : (
                                <span className="text-4xl font-bold text-gray-400">
                                    {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                                </span>
                            )}
                        </div>

                        {/* Upload Button Overlay */}
                        <label className="absolute bottom-0 right-0 bg-indigo-600 rounded-full p-2 cursor-pointer shadow-md hover:bg-indigo-700 transition-colors">
                            <input
                                type="file"
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileSelect}
                                disabled={photoUploading}
                            />
                            {photoUploading ? (
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                            )}
                        </label>
                    </div>

                    {/* Basic Info */}
                    <div className="text-center sm:text-left flex-1 min-w-0">
                        <h3 className="text-2xl font-bold text-gray-900 truncate">{user.name}</h3>
                        <p className="text-indigo-600 font-medium">{user.designation_title || (user.role === 'advisor' ? 'User Designation' : 'Designation')}</p>
                        <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-4 text-sm text-gray-500">
                            {user.role !== 'health_plus_partner' && (
                                <div className="flex items-center">
                                    <span className="font-semibold mr-2 text-gray-700">WealthPlus ID:</span>
                                    <span className="bg-gray-100 px-2 py-1 rounded">{user.wealthplus_id || 'N/A'}</span>
                                </div>
                            )}
                            <div className="flex items-center">
                                <span className="font-semibold mr-2 text-gray-700">Role:</span>
                                <span className="capitalize">{user.role}</span>
                            </div>
                        </div>
                    </div>

                    {/* Header Action */}
                    <div>
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Edit Profile
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Details Form Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                    <p className="mt-1 text-sm text-gray-500">Update your contact details here.</p>
                </div>

                <div className="p-6">
                    {isEditing ? (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="your@email.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Phone number"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">This number will appear on your customized posters.</p>
                                </div>

                                {user.role !== 'health_plus_partner' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">WealthPlus ID</label>
                                        <input
                                            type="text"
                                            value={user.wealthplus_id || ''}
                                            disabled
                                            className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 text-gray-500 sm:text-sm cursor-not-allowed"
                                            title="Contact Super Admin to change WealthPlus ID"
                                        />
                                        <p className="mt-1 text-xs text-gray-400">Read-only field.</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Designation</label>
                                    <input
                                        type="text"
                                        value={user.designation_title || ''}
                                        disabled
                                        className="mt-1 block w-full border border-gray-300 bg-gray-50 rounded-md shadow-sm py-2 px-3 text-gray-500 sm:text-sm cursor-not-allowed"
                                        title="Contact Super Admin to change designation"
                                    />
                                    <p className="mt-1 text-xs text-gray-400">Read-only field.</p>
                                </div>
                            </div>

                            {/* Nominee Information Section */}
                            <div className="border-t border-gray-200 pt-6 mt-6">
                                <h4 className="text-md font-medium text-gray-900 mb-4">Nominee Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Nominee Name</label>
                                        <input
                                            type="text"
                                            name="nominee_name"
                                            value={formData.nominee_name}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Full name of nominee"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Nominee Phone</label>
                                        <input
                                            type="tel"
                                            name="nominee_phone"
                                            value={formData.nominee_phone}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                            placeholder="Nominee contact number"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Nominee DOB</label>
                                        <input
                                            type="date"
                                            name="nominee_dob"
                                            value={formData.nominee_dob}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Relationship</label>
                                        <select
                                            name="nominee_relationship"
                                            value={formData.nominee_relationship}
                                            onChange={handleInputChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        >
                                            <option value="">Select relationship</option>
                                            <option value="Spouse">Spouse</option>
                                            <option value="Parent">Parent</option>
                                            <option value="Child">Child</option>
                                            <option value="Sibling">Sibling</option>
                                            <option value="Other">Other</option>
                                        </select>
                                        {formData.nominee_relationship === 'Other' && (
                                            <input
                                                type="text"
                                                name="nominee_relationship_custom"
                                                value={formData.nominee_relationship_custom}
                                                onChange={handleInputChange}
                                                className="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                placeholder="Please specify relationship"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditing(false);
                                        // Reset form data
                                        const predefinedRelationships = ['Spouse', 'Parent', 'Child', 'Sibling', ''];
                                        const isCustom = user.nominee_relationship && !predefinedRelationships.includes(user.nominee_relationship);

                                        setFormData({
                                            email: user.email || '',
                                            phone: user.phone || '',
                                            nominee_name: user.nominee_name || '',
                                            nominee_phone: user.nominee_phone || '',
                                            nominee_dob: user.nominee_dob ? new Date(user.nominee_dob).toISOString().split('T')[0] : '',
                                            nominee_relationship: isCustom ? 'Other' : (user.nominee_relationship || ''),
                                            nominee_relationship_custom: isCustom ? user.nominee_relationship : ''
                                        });
                                    }}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400"
                                >
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Email Address</h4>
                                    <p className="mt-1 text-sm text-gray-900">{user.email || 'Not provided'}</p>
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Phone Number</h4>
                                    <p className="mt-1 text-sm text-gray-900">{user.phone}</p>
                                </div>
                                {user.role !== 'health_plus_partner' && (
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">WealthPlus ID</h4>
                                        <p className="mt-1 text-sm text-gray-900">{user.wealthplus_id || 'N/A'}</p>
                                    </div>
                                )}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-500">Designation</h4>
                                    <p className="mt-1 text-sm text-gray-900">{user.designation_title || (user.role === 'advisor' ? 'User Designation' : 'Designation')}</p>
                                </div>
                            </div>

                            {/* Nominee Information Display */}
                            <div className="border-t border-gray-200 pt-6 mt-6">
                                <h4 className="text-md font-medium text-gray-900 mb-4">Nominee Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Nominee Name</h4>
                                        <p className="mt-1 text-sm text-gray-900">{user.nominee_name || 'Not provided'}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Nominee Phone</h4>
                                        <p className="mt-1 text-sm text-gray-900">{user.nominee_phone || 'Not provided'}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Nominee DOB</h4>
                                        <p className="mt-1 text-sm text-gray-900">{user.nominee_dob ? new Date(user.nominee_dob).toLocaleDateString() : 'Not provided'}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-500">Relationship</h4>
                                        <p className="mt-1 text-sm text-gray-900">{user.nominee_relationship || 'Not provided'}</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Image Cropper Modal */}
            {showCropper && selectedImage && (
                <ImageCropper
                    image={selectedImage}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCancelCrop}
                />
            )}
        </div>
    );
};

export default ProfileTab;
